import React, { useState, memo } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
type Question = {
  id: number;
  module: string;
  emoji: string;
  text: string;
  options: string[];
};
const QUESTIONS: Question[] = [
// Module 1: Home Wi-Fi Network
{
  id: 0,
  module: 'Home Wi-Fi Network',
  emoji: '🏠',
  text: 'Does your home Wi‑Fi require a password to connect?',
  options: ['Yes', 'No', "Don't know"]
}, {
  id: 1,
  module: 'Home Wi-Fi Network',
  emoji: '🏠',
  text: 'Did you change your Wi-Fi name from the default?',
  options: ['Yes', 'No', "Don't know"]
}, {
  id: 2,
  module: 'Home Wi-Fi Network',
  emoji: '🏠',
  text: 'Do you have a separate Wi‑Fi network for guests or smart devices?',
  options: ['Yes', 'No', "Don't know"]
}, {
  id: 3,
  module: 'Home Wi-Fi Network',
  emoji: '🏠',
  text: 'Does your router update automatically?',
  options: ['Yes', 'No', "Don't know"]
},
// Module 2: Passwords
{
  id: 4,
  module: 'Passwords',
  emoji: '🔐',
  text: 'How do you manage your passwords?',
  options: ['Password manager', 'Unique passwords memorized', 'Same/reused passwords', 'Written down on paper']
}, {
  id: 5,
  module: 'Passwords',
  emoji: '🔐',
  text: 'Do you have two‑factor authentication (2FA) turned on for important accounts (email, banking, shopping)?',
  options: ['Yes for all important accounts', 'Yes for some accounts', 'No', "Don't know"]
}, {
  id: 6,
  module: 'Passwords',
  emoji: '🔐',
  text: 'If yes to 2FA, how do you usually get codes?',
  options: ['Authenticator app', 'Hardware key', 'Text message (SMS)', 'Other']
}, {
  id: 7,
  module: 'Passwords',
  emoji: '🔐',
  text: 'Have you checked if your email/passwords were exposed in a data breach?',
  options: ['Yes', 'No', "Don't know"]
},
// Module 3: Devices
{
  id: 8,
  module: 'Devices',
  emoji: '📱',
  text: 'Do you use a passcode, fingerprint, or face recognition to unlock your phone?',
  options: ['Yes', 'No']
}, {
  id: 9,
  module: 'Devices',
  emoji: '📱',
  text: 'Are automatic updates turned on across all of your devices?',
  options: ['Yes', 'No', "Don't know"]
}, {
  id: 10,
  module: 'Devices',
  emoji: '📱',
  text: 'Do you regularly back up your phone (iCloud, Google Drive, other)?',
  options: ['Yes', 'No', "Don't know"]
}, {
  id: 11,
  module: 'Devices',
  emoji: '📱',
  text: 'Do you back up your computer (cloud or external drive)?',
  options: ['Yes', 'No', "Don't know"]
},
// Module 4: Privacy & Data
{
  id: 12,
  module: 'Privacy & Data',
  emoji: '🛡️',
  text: 'Do you download apps/software only from official stores or trusted sites?',
  options: ['Yes', 'No', "Don't know"]
}, {
  id: 13,
  module: 'Privacy & Data',
  emoji: '🛡️',
  text: 'Do you ever connect to public Wi‑Fi with your phone, tablet, or computer?',
  options: ['Never', 'Rarely', 'Sometimes', 'Often']
}, {
  id: 14,
  module: 'Privacy & Data',
  emoji: '🛡️',
  text: 'Does your family use a safe word for emergencies?',
  options: ['Yes', 'No', "Don't know", 'Not applicable']
}, {
  id: 15,
  module: 'Privacy & Data',
  emoji: '🛡️',
  text: 'Have you checked if your credentials were in a breach?',
  options: ['Yes', 'No', "Don't know"]
}];
export function AssessmentQuizPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [showingTransition, setShowingTransition] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const question = QUESTIONS[currentQuestion];
  const progressPercent = 30 + currentQuestion / QUESTIONS.length * 70;
  const handleSelect = (option: string) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion]: option
    }));
    setTimeout(() => {
      if (currentQuestion === QUESTIONS.length - 1) {
        setIsComplete(true);
      } else if (currentQuestion === 3 || currentQuestion === 7 || currentQuestion === 11) {
        setShowingTransition(true);
        setCurrentQuestion(prev => prev + 1);
      } else {
        setCurrentQuestion(prev => prev + 1);
      }
    }, 400);
  };
  const handleBack = () => {
    if (isComplete) {
      setIsComplete(false);
      setCurrentQuestion(QUESTIONS.length - 1);
    } else if (showingTransition) {
      setShowingTransition(false);
      setCurrentQuestion(prev => prev - 1);
    } else if (currentQuestion === 0) {
      navigate('/assessment/transition', {
        state: location.state
      });
    } else if (currentQuestion === 4 || currentQuestion === 8 || currentQuestion === 12) {
      // Skip transition screen when going back
      setCurrentQuestion(prev => prev - 1);
    } else {
      setCurrentQuestion(prev => prev - 1);
    }
  };
  const handleTransitionContinue = () => {
    setShowingTransition(false);
  };
  const handleComplete = () => {
    navigate('/assessment/results', {
      state: {
        answers
      }
    });
  };
  if (isComplete) {
    return <div className="w-full min-h-screen bg-[#f5f5f7] flex flex-col" data-id="element-515">
        {/* Top bar */}
        <div className="w-full px-5 pt-4 pb-2" data-id="element-516">
          <div className="max-w-md mx-auto flex items-center gap-4" data-id="element-517">
            <button onClick={handleBack} className="flex items-center justify-center w-10 h-10 -ml-2 text-gray-500 hover:text-[#631bb7] transition-colors" aria-label="Go back" data-id="element-518">
              <ArrowLeftIcon size={22} data-id="element-519" />
            </button>
            {/* Progress bar */}
            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-520">
              <div className="h-full bg-[#631bb7] rounded-full transition-all duration-500" style={{
              width: '100%'
            }} data-id="element-521" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-5 pt-4 pb-6 flex flex-col" data-id="element-522">
          <div className="max-w-md mx-auto text-center flex-1 flex flex-col" data-id="element-523">
            <div className="flex-1 flex flex-col items-center justify-center" data-id="element-524">
              <img src="https://cdn.magicpatterns.com/uploads/qm8eGpXKnKzyXLYb5H58w8/Caveo_Icon_Mark_48x39_Full_Color_PNG.png" alt="Caveo" className="w-12 h-auto mx-auto mb-4" data-id="element-525" />
              <h1 className="md:text-[26px] text-[22px] font-[600] text-[#190647] mb-3" data-id="element-526">
                Assessment Complete!
              </h1>
              <p className="text-gray-500 text-sm md:text-base leading-relaxed mb-6" data-id="element-527">
                Great job! We now have everything we need to build your
                personalized security plan.
              </p>
            </div>

            <div className="mt-auto pt-4" data-id="element-528">
              <button onClick={handleComplete} className="w-full py-3.5 rounded-xl font-semibold text-white bg-[#631bb7] hover:bg-[#521099] transition-all shadow-md" data-id="element-529">
                See My Results
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  if (showingTransition) {
    return <div className="w-full min-h-screen bg-[#f5f5f7] flex flex-col" data-id="element-530">
        {/* Top bar */}
        <div className="w-full px-5 pt-4 pb-2" data-id="element-531">
          <div className="max-w-md mx-auto flex items-center gap-4" data-id="element-532">
            <button onClick={handleBack} className="flex items-center justify-center w-10 h-10 -ml-2 text-gray-500 hover:text-[#631bb7] transition-colors" aria-label="Go back" data-id="element-533">
              <ArrowLeftIcon size={22} data-id="element-534" />
            </button>
            {/* Progress bar */}
            <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-535">
              <div className="h-full bg-[#631bb7] rounded-full transition-all duration-500" style={{
              width: `${progressPercent}%`
            }} data-id="element-536" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 px-5 pt-4 pb-6 flex flex-col" data-id="element-537">
          <div className="max-w-md mx-auto text-center flex-1 flex flex-col" data-id="element-538">
            <div className="flex-1 flex flex-col items-center justify-center" data-id="element-539">
              <img src="https://cdn.magicpatterns.com/uploads/qm8eGpXKnKzyXLYb5H58w8/Caveo_Icon_Mark_48x39_Full_Color_PNG.png" alt="Caveo" className="w-12 h-auto mx-auto mb-4" data-id="element-540" />
              <h1 className="md:text-[26px] text-[22px] font-[600] text-[#190647] mb-3" data-id="element-541">
                Great progress!
              </h1>
              <p className="text-gray-500 text-base md:text-lg leading-relaxed" data-id="element-542">
                Next up: <br data-id="element-543" />
                <span className="inline-flex items-center gap-1.5 mt-2 text-[#190647] font-[600]" data-id="element-544">
                  {question.emoji} {question.module}
                </span>
              </p>
            </div>

            <div className="mt-auto pt-4" data-id="element-545">
              <button onClick={handleTransitionContinue} className="w-full py-3.5 rounded-xl font-semibold text-white bg-[#631bb7] hover:bg-[#521099] transition-all shadow-md" data-id="element-546">
                Let's Continue
              </button>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="w-full min-h-screen bg-[#f5f5f7] flex flex-col" data-id="element-547">
      {/* Top bar */}
      <div className="w-full px-5 pt-4 pb-2" data-id="element-548">
        <div className="max-w-md mx-auto flex items-center gap-4" data-id="element-549">
          <button onClick={handleBack} className="flex items-center justify-center w-10 h-10 -ml-2 text-gray-500 hover:text-[#631bb7] transition-colors" aria-label="Go back" data-id="element-550">
            <ArrowLeftIcon size={22} data-id="element-551" />
          </button>
          {/* Progress bar */}
          <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-552">
            <div className="h-full bg-[#631bb7] rounded-full transition-all duration-500" style={{
            width: `${progressPercent}%`
          }} data-id="element-553" />
          </div>
        </div>
      </div>

      {/* Question Area */}
      <div className="flex-1 px-5 pt-3 pb-6 flex flex-col" data-id="element-554">
        <div className="max-w-md mx-auto w-full flex-1 flex flex-col" data-id="element-555">
          {/* Module Label */}
          <div className="text-xs font-[600] text-[#631bb7] uppercase tracking-wider mb-2 text-center" data-id="element-556">
            {question.emoji} {question.module}
          </div>

          <h1 className="md:text-[24px] text-[20px] font-[600] text-[#190647] mb-5 text-center" data-id="element-557">
            {question.text}
          </h1>

          {/* Options */}
          <div className="space-y-3" data-id="element-558">
            {question.options.map((option, i) => {
            const isSelected = answers[currentQuestion] === option;
            return <button key={i} onClick={() => handleSelect(option)} className={`w-full flex items-center px-5 py-3.5 rounded-2xl text-left transition-all duration-200 border-2 ${isSelected ? 'bg-[#631bb7] border-[#631bb7] text-white shadow-md' : 'bg-white border-gray-100 text-[#111111] hover:border-gray-200'}`} data-id="element-559">
                  <span className="text-[15px] leading-snug font-[500]" data-id="element-560">
                    {option}
                  </span>
                </button>;
          })}
          </div>
        </div>
      </div>
    </div>;
}